import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import { Phone, CheckCircle2, FileText, ShieldCheck } from "lucide-react";
const DoesFederalBcbsCoverSaint = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "Does Federal Blue Cross Blue Shield Cover SAINT? | Neuro Wellness TMS Centers of America"
        }
        description={
          "If you have federal BCBS (FEP Blue), SAINT TMS may be covered when certain criteria are met. We verify your benefits for free — call 954-827-9793."
        }
        canonical={"/does-federal-bcbs-cover-saint"}
        keywords={
          "does federal BCBS cover SAINT, FEP Blue TMS coverage, is SAINT covered by insurance, federal employee health benefits depression treatment"
        }
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "Does Federal Blue Cross Blue Shield Cover SAINT?",
            description:
              "FEP medical policy and SAINT accelerated TMS coverage for federal employees and annuitants.",
            about: [
              "Federal Employee Health Benefits",
              "SAINT TMS",
              "Insurance Coverage",
            ],
            audience: {
              "@type": "Patient",
              name: "Federal employees, annuitants, and their families seeking depression treatment coverage information",
            },
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Does Federal Blue Cross Blue Shield cover SAINT TMS?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "SAINT may be covered when FEP medical necessity criteria are met. FEP's medical policy names the SAINT protocol as an example of accelerated theta burst stimulation. Coverage is not guaranteed and requires verification of your specific plan.",
                },
              },
              {
                "@type": "Question",
                name: "What are the FEP medical necessity criteria for TMS?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "FEP requires a confirmed diagnosis of severe major depressive disorder, inadequate response to two antidepressants from different classes or inability to tolerate them, and a failed trial of psychotherapy known to be effective for major depressive disorder.",
                },
              },
            ],
          },
        ]}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              Federal Employee Health Benefits
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              Does Federal Blue Cross Blue Shield Cover SAINT?
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              The short answer: it may. Here's what you need to know — and how
              we help you find out for sure, at no cost.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-transparent px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
              >
                <Phone className="h-5 w-5" />
                Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        {/* Body */}
        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            {/* Plain answer */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <p>
                If you have Federal Employee Health Benefits (FEHB) through FEP
                Blue — Standard, Basic, or Focus — SAINT may be covered when
                medical necessity criteria are met. We'll give you the plain
                answer first, then the details.
              </p>
              <p>
                FEP's medical policy specifically names the SAINT protocol.
                That's unusual — many commercial and local Blue plans still
                classify accelerated TMS as experimental. FEP does not. Their
                policy describes SAINT as an example of an accelerated TMS
                protocol that may be used when the criteria below are met.
              </p>
              <p>
                That's good news. But policy language isn't a guarantee of
                payment. Your specific plan, your local Blue carrier, and your
                clinical documentation all matter. That's why we verify your
                benefits before you commit to anything.
              </p>
            </div>

            {/* Three criteria — plain English */}
            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                What FEP looks for
              </h2>
              <p className="mt-3 text-[17px] leading-relaxed text-muted-foreground">
                FEP uses three main criteria to decide whether TMS — including
                SAINT — is medically necessary. You don't need to figure these
                out alone. Our team helps you gather what's needed.
              </p>
              <div className="mt-6 space-y-6">
                <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
                    <div>
                      <h3 className="font-headings text-lg font-bold text-primary">
                        1. A confirmed diagnosis of severe depression
                      </h3>
                      <p className="mt-2 text-[16px] leading-relaxed text-muted-foreground">
                        Your diagnosis needs to be severe major depressive
                        disorder, documented by your provider using standard
                        rating scales. This is a clinical determination — not
                        something you assess yourself.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
                    <div>
                      <h3 className="font-headings text-lg font-bold text-primary">
                        2. Antidepressants haven't worked well enough
                      </h3>
                      <p className="mt-2 text-[16px] leading-relaxed text-muted-foreground">
                        You need to meet at least one of these:
                      </p>
                      <ul className="mt-3 space-y-2">
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          <span className="text-[16px] leading-relaxed text-muted-foreground">
                            You've tried at least two antidepressants from
                            different classes, for at least six weeks each at a
                            proper dose, without enough improvement
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          <span className="text-[16px] leading-relaxed text-muted-foreground">
                            You couldn't tolerate a therapeutic dose because of
                            side effects
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          <span className="text-[16px] leading-relaxed text-muted-foreground">
                            You've responded to TMS before, in a previous
                            episode at least three months ago
                          </span>
                        </li>
                        <li className="flex items-start gap-3">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                          <span className="text-[16px] leading-relaxed text-muted-foreground">
                            You're a candidate for ECT, but ECT wouldn't be a
                            better option for you
                          </span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-accent" />
                    <div>
                      <h3 className="font-headings text-lg font-bold text-primary">
                        3. You've also tried therapy
                      </h3>
                      <p className="mt-2 text-[16px] leading-relaxed text-muted-foreground">
                        FEP also requires that you've had a trial of
                        psychotherapy — talk therapy known to be effective for
                        depression — for an adequate length of time, documented
                        by your provider. This carries the same weight as the
                        medication requirement. If your records don't show a
                        therapy trial, that's a gap we'll help you address
                        before submitting for authorization.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Honest limitations — simplified */}
            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                What we can't promise
              </h2>
              <div className="mt-4 space-y-4 text-[17px] leading-relaxed text-muted-foreground">
                <p>
                  SAINT uses newer billing codes that are still being adopted
                  across the insurance industry. A policy that names the
                  protocol is a strong signal — but it's not the same as a
                  guarantee that your specific claim will be paid.
                </p>
                <p>
                  Whether you need prior authorization depends on your specific
                  FEP option and the local Blue plan on the back of your card.
                  We can't tell you it's never required, because for your plan
                  it may be.
                </p>
              </div>
            </div>

            {/* What we do */}
            <div className="rounded-2xl border-2 border-accent/30 bg-accent/5 p-6 sm:p-8">
              <div className="flex items-start gap-3">
                <ShieldCheck className="mt-0.5 h-7 w-7 shrink-0 text-accent" />
                <div>
                  <h2 className="font-headings text-xl font-bold text-primary">
                    What we do for you — at no cost
                  </h2>
                  <p className="mt-2 text-[17px] leading-relaxed text-muted-foreground">
                    When you reach out, our team will:
                  </p>
                  <ul className="mt-3 space-y-2">
                    {[
                      "Contact your FEP Blue plan to confirm whether SAINT is covered under your specific option",
                      "Check whether prior authorization is required by your local Blue carrier",
                      "Identify what documentation is needed and help you gather it",
                      "Give you a written summary of what we found — including any out-of-pocket costs — before you commit to anything",
                      "Submit the authorization request on your behalf",
                      "Handle the appeal process if it's initially denied",
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
                        <span className="text-[16px] leading-relaxed text-muted-foreground">
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-4 text-[16px] leading-relaxed text-muted-foreground">
                    Most denials under FEP come from incomplete documentation —
                    not genuine ineligibility. Appeals with fuller records often
                    succeed.
                  </p>
                </div>
              </div>
            </div>

            {/* Related pages */}
            <div>
              <h2 className="font-headings text-2xl font-bold text-primary">
                Learn more
              </h2>
              <div className="mt-4 flex flex-col gap-3">
                <Link
                  to="/learn/saint-vs-traditional-tms"
                  className="text-accent hover:underline"
                >
                  SAINT vs. Standard TMS — What's the difference? →
                </Link>
                <Link to="/what-is-tms" className="text-accent hover:underline">
                  What Is TMS? A Simple Explanation →
                </Link>
                <Link
                  to="/how-insurance-works"
                  className="text-accent hover:underline"
                >
                  How Insurance Verification Works →
                </Link>
                <Link
                  to="/federal-employee-mental-health"
                  className="text-accent hover:underline"
                >
                  Federal Employee Mental Health Benefits →
                </Link>
              </div>
            </div>

            <div className="rounded-2xl border border-light-blue bg-light-blue/30 p-6">
              <div className="flex items-start gap-3">
                <FileText className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <p className="text-[15px] leading-relaxed text-muted-foreground">
                  Coverage information current as of August 2026. FEP medical
                  policy is revised annually. This page is education, not
                  medical advice or a coverage determination. Your specific
                  benefits depend on your plan, your local Blue carrier, and
                  your clinical documentation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-navy py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl text-center">
            <h2 className="font-headings text-2xl font-bold text-white sm:text-3xl">
              Let's check your benefits — for free
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-base text-primary-foreground/80 sm:text-lg">
              No commitment. We'll contact your FEP Blue plan, confirm whether
              SAINT is covered under your option, and tell you what your costs
              would be before you decide anything.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-transparent px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
              >
                <Phone className="h-5 w-5" />
                Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default DoesFederalBcbsCoverSaint;
